import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, UserPlus, X, Shield, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";

export default function AdminTeams() {
    const [teams, setTeams] = useState(null);
    const [name, setName] = useState("");
    const [tag, setTag] = useState("");
    const [active, setActive] = useState(null);
    const [memberEmail, setMemberEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/teams").then(({ data }) => setTeams(data.teams));
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try { await api.post("/admin/teams", { name, tag }); setName(""); setTag(""); toast.success("Team created."); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not create team."); }
        finally { setBusy(false); }
    };

    const removeTeam = async (id) => {
        try { await api.delete(`/admin/teams/${id}`); toast("Team removed."); setActive(null); load(); }
        catch { toast.error("Could not remove team."); }
    };

    const addMember = async () => {
        if (!memberEmail.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/teams/${active.id}/members`, { email: memberEmail.trim() });
            setActive(data); setMemberEmail(""); toast.success("Member added."); load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not add member."); }
        finally { setBusy(false); }
    };

    const removeMember = async (uid) => {
        try {
            const { data } = await api.delete(`/admin/teams/${active.id}/members/${uid}`);
            setActive(data); load();
        } catch { toast.error("Could not remove member."); }
    };

    if (!teams) return <Loader label="Loading teams" />;

    return (
        <div className="ww-fade-up" data-testid="admin-teams">
            <PageHeader title="Teams & Squads" subtitle="Group players into teams for management and coaching." />

            <div className="bg-surface border border-border rounded-sm p-5 mb-6">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-volt" /> Create Team</h3>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name" data-testid="team-name-input"
                        className="bg-obsidian border-border rounded-sm h-10 flex-1" />
                    <Input value={tag} onChange={(e) => setTag(e.target.value.toUpperCase().slice(0, 8))} placeholder="TAG" data-testid="team-tag-input"
                        className="bg-obsidian border-border rounded-sm h-10 w-full sm:w-28 font-mono" />
                    <button onClick={create} disabled={busy} data-testid="team-create-btn"
                        className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
                    </button>
                </div>
            </div>

            {teams.length === 0 ? (
                <div className="bg-surface border border-border rounded-sm px-5 py-12 text-center text-sm text-muted-foreground">
                    No teams yet. Create your first squad above.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teams.map((t) => (
                        <button key={t.id} onClick={() => setActive(t)} data-testid={`team-card-${t.id}`}
                            className="text-left bg-surface border border-border rounded-sm p-5 hover:border-volt/40 transition-colors">
                            <div className="flex items-center gap-2 mb-1">
                                <Shield className="w-4 h-4 text-volt" />
                                <span className="font-heading font-bold text-white">{t.name}</span>
                                {t.tag && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{t.tag}</span>}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {t.member_count} member{t.member_count === 1 ? "" : "s"}</div>
                        </button>
                    ))}
                </div>
            )}

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2"><Shield className="w-5 h-5 text-volt" /> {active?.name}
                            {active?.tag && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{active.tag}</span>}
                        </DialogTitle>
                        <DialogDescription className="text-xs">{active?.member_count} members</DialogDescription>
                    </DialogHeader>
                    {active && (
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <Input value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} placeholder="Add member by email" data-testid="team-member-email"
                                    className="bg-obsidian border-border rounded-sm h-9 flex-1" />
                                <button onClick={addMember} disabled={busy} data-testid="team-add-member"
                                    className="h-9 px-4 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 text-sm flex items-center gap-1.5 disabled:opacity-50">
                                    <UserPlus className="w-4 h-4" /> Add
                                </button>
                            </div>
                            <div className="space-y-1.5 max-h-64 overflow-y-auto">
                                {active.members.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No members yet.</div>}
                                {active.members.map((m) => (
                                    <div key={m.id} className="flex items-center justify-between bg-obsidian border border-border rounded-sm px-3 py-2" data-testid={`member-${m.id}`}>
                                        <div>
                                            <div className="text-sm text-white">{m.name} {m.team_role === "captain" && <span className="text-[10px] text-volt font-mono">CAPTAIN</span>}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{m.email}</div>
                                        </div>
                                        <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`} className="text-muted-foreground hover:text-alert"><X className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <button data-testid="team-delete-btn" className="w-full h-9 rounded-sm border border-alert/50 text-alert hover:bg-alert/10 text-sm flex items-center justify-center gap-2">
                                        <Trash2 className="w-4 h-4" /> Delete Team
                                    </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-surface2 border-border">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Delete {active.name}?</AlertDialogTitle>
                                        <AlertDialogDescription>Members are unassigned but their accounts/data are kept. This cannot be undone.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel className="bg-transparent border-border">Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => removeTeam(active.id)} data-testid="team-delete-confirm" className="bg-alert text-white hover:bg-[#ff4d7a]">Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
